/* ═══════════════════════════════════════════════════════════════════
   BurstPlanVisualizer — the hero visual for the Wave Orchestrator.
   Renders:
     1. Unique hook pill + velocity band chip
     2. Predicted wave shape as an area chart (72h velocity curve)
     3. Burst timeline — each burst shown as a tile with creator chips
        and their per-platform post offsets
     4. Rationale bullet list
   ═══════════════════════════════════════════════════════════════════ */
import React, { useMemo } from 'react';
import { Sparkles, Zap, Users, Clock, Hash, Activity, MessageCircle, Star } from 'lucide-react';
import { T, P, CARD, BORDER } from './constants';
import DiversityScorePill from './DiversityScorePill';

const ANGLE_COLORS = {
  pov: '#FF6BBD',
  tutorial: '#00F5FF',
  reaction: '#FFB84D',
  confession: '#C77DFF',
  day_in_the_life: '#7DE88C',
};

const ROLE_BADGES = {
  patient_zero: { label: 'Patient Zero', icon: Star, bg: 'rgba(255, 107, 189, 0.15)', color: '#FF6BBD' },
  amplifier: { label: 'Amplifier', icon: Zap, bg: 'rgba(0, 245, 255, 0.10)', color: '#00F5FF' },
  engagement_prompter: { label: 'Reply Prompter', icon: MessageCircle, bg: 'rgba(125, 232, 140, 0.15)', color: '#7DE88C' },
};

const BAND_COPY = {
  awareness:  { label: 'Awareness burst band', tagline: 'Wide spread, sustained cadence' },
  conversion: { label: 'Conversion burst band', tagline: 'High density, tight spikes' },
  community:  { label: 'Community burst band', tagline: 'Multi-burst over longer window' },
};

function PredictedShapeChart({ samples }) {
  const { pathD, fillD, maxHour, peaks, gridLines } = useMemo(() => {
    if (!samples || samples.length === 0) return { pathD: '', fillD: '', maxHour: 1, peaks: [], gridLines: [] };
    const maxH = Math.max(...samples.map(s => s.hour_offset)) || 1;
    const maxV = Math.max(...samples.map(s => s.predicted_velocity)) || 1;
    const W = 100, H = 50;
    const points = samples.map(s => ({
      x: (s.hour_offset / maxH) * W,
      y: H - (s.predicted_velocity / maxV) * H,
    }));
    const pd = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
    const fd = `${pd} L ${W} ${H} L 0 ${H} Z`;
    // Identify peak hours (local maxima)
    const pks = [];
    for (let i = 1; i < samples.length - 1; i++) {
      if (samples[i].predicted_velocity > samples[i - 1].predicted_velocity &&
          samples[i].predicted_velocity >= samples[i + 1].predicted_velocity &&
          samples[i].predicted_velocity > 0.6) {
        pks.push({ x: (samples[i].hour_offset / maxH) * W, hour: samples[i].hour_offset });
      }
    }
    // Always mark hour 0
    if (samples[0]) pks.unshift({ x: 0, hour: 0 });
    // Gridlines — visible vertical hour markers
    const gridLines = [0, 12, 24, 36, 48, 60, 72]
      .filter(h => h <= maxH)
      .map(h => ({ h, x: (h / maxH) * 100 }));
    return { pathD: pd, fillD: fd, maxHour: maxH, peaks: pks, gridLines };
  }, [samples]);

  return (
    <div className="rounded-xl p-5" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="predicted-shape-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4" style={{ color: T }} />
          <span className="text-xs font-bold text-white uppercase tracking-wide">Predicted Wave Shape</span>
        </div>
        <span className="text-[10px] text-gray-500 uppercase font-semibold">{maxHour}h window</span>
      </div>
      <svg viewBox="0 0 100 56" className="w-full" preserveAspectRatio="none" style={{ height: 160 }}>
        <defs>
          <linearGradient id="velFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={P} stopOpacity="0.6" />
            <stop offset="100%" stopColor={P} stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="velStroke" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={T} stopOpacity="1" />
            <stop offset="100%" stopColor={P} stopOpacity="1" />
          </linearGradient>
        </defs>
        {/* grid */}
        {gridLines.map(g => (
          <line key={g.h} x1={g.x} x2={g.x} y1="0" y2="50" stroke="#1a1a1a" strokeWidth="0.2" />
        ))}
        {/* area */}
        <path d={fillD} fill="url(#velFill)" />
        <path d={pathD} fill="none" stroke="url(#velStroke)" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
        {/* burst peak markers */}
        {peaks.map((pk, i) => (
          <g key={pk.hour}>
            <line x1={pk.x} x2={pk.x} y1="0" y2="50" stroke={T} strokeWidth="0.3" strokeDasharray="1,1" opacity="0.5" />
            <circle cx={pk.x} cy="50" r="0.9" fill={T} />
            <text x={pk.x} y="55" fill="#888" fontSize="2.2" textAnchor={i === 0 ? 'start' : 'middle'}>
              B{i + 1}
            </text>
          </g>
        ))}
      </svg>
      <div className="flex items-center justify-between mt-2 text-[9px] text-gray-600 uppercase tracking-wider font-semibold">
        <span>Burst 1 fires</span>
        <span>Reacceleration windows</span>
        <span>Decay tail</span>
      </div>
    </div>
  );
}

function BurstTile({ burst, burstIndex }) {
  // Group per-creator: one card per creator with their per-platform post offsets
  const byCreator = useMemo(() => {
    const map = new Map();
    const burstStart = new Date(burst.scheduled_start).getTime();
    burst.assignments.forEach(a => {
      const postMs = new Date(a.post_time).getTime();
      const offsetSec = Math.round((postMs - burstStart) / 1000);
      if (!map.has(a.creator_handle)) {
        map.set(a.creator_handle, {
          handle: a.creator_handle,
          name: a.creator_name,
          role: a.role,
          angle: a.angle,
          cta_required: a.cta_required,
          platforms: [],
        });
      }
      map.get(a.creator_handle).platforms.push({ platform: a.platform, offsetSec });
    });
    return Array.from(map.values()).sort((a, b) => {
      const aOffset = Math.min(...a.platforms.map(p => p.offsetSec));
      const bOffset = Math.min(...b.platforms.map(p => p.offsetSec));
      return aOffset - bOffset;
    });
  }, [burst]);

  const windowSec = burst.window_seconds;
  const scheduledDate = new Date(burst.scheduled_start);
  const dateStr = scheduledDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = scheduledDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="rounded-xl p-4" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid={`burst-tile-${burst.burst_number}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                 style={{ background: `linear-gradient(135deg, ${T}, ${P})`, color: '#000' }}>
              {burst.burst_number}
            </div>
            <span className="text-sm font-bold text-white">Burst {burst.burst_number}</span>
          </div>
          <div className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider">
            {dateStr} · {timeStr} · {windowSec}s window
          </div>
        </div>
        <div className="flex gap-1.5">
          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                style={{ background: 'rgba(255, 107, 189, 0.15)', color: P }}>
            {burst.role_mix.unique_creators} creators
          </span>
        </div>
      </div>

      {/* Per-creator timeline */}
      <div className="space-y-2">
        {byCreator.map((creator) => {
          const roleBadge = ROLE_BADGES[creator.role] || ROLE_BADGES.amplifier;
          const RoleIcon = roleBadge.icon;
          const angleColor = ANGLE_COLORS[creator.angle] || '#888';
          const minOffset = Math.min(...creator.platforms.map(p => p.offsetSec));
          const leftPct = (minOffset / windowSec) * 100;
          return (
            <div key={creator.handle} className="group">
              {/* Creator header line */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-white">{creator.handle}</span>
                  <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase flex items-center gap-1"
                        style={{ background: roleBadge.bg, color: roleBadge.color }}>
                    <RoleIcon className="h-2.5 w-2.5" />
                    {roleBadge.label}
                  </span>
                  {creator.cta_required && (
                    <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase"
                          style={{ background: 'rgba(255, 184, 77, 0.18)', color: '#FFB84D' }}>
                      CTA
                    </span>
                  )}
                </div>
                <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase"
                      style={{ background: `${angleColor}22`, color: angleColor }}>
                  {creator.angle.replace(/_/g, ' ')}
                </span>
              </div>
              {/* Timeline bar */}
              <div className="relative h-6 rounded overflow-hidden" style={{ background: '#1a1a1a' }}>
                {/* Burst window background */}
                <div className="absolute inset-0 opacity-20"
                     style={{ background: `linear-gradient(90deg, ${T}22, ${P}22)` }} />
                {/* Platform post dots */}
                {creator.platforms.map((p) => {
                  const pct = (p.offsetSec / windowSec) * 100;
                  const platformIcon = p.platform === 'tiktok' ? 'TT' : p.platform === 'instagram' ? 'IG' : 'YT';
                  const platformColor = p.platform === 'tiktok' ? '#FF0050' : p.platform === 'instagram' ? '#E4405F' : '#FF0000';
                  return (
                    <div key={`${creator.handle}-${p.platform}`}
                         className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all hover:scale-125"
                         style={{ left: `${Math.max(0, Math.min(100, pct))}%` }}
                         title={`${p.platform} @ +${p.offsetSec}s`}>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                           style={{ background: platformColor, border: `1px solid ${angleColor}` }}>
                        {platformIcon}
                      </div>
                    </div>
                  );
                })}
                {/* Offset label on the left */}
                <div className="absolute inset-y-0 left-1 flex items-center text-[8px] text-gray-500 font-mono">
                  +{Math.min(...creator.platforms.map(p => p.offsetSec))}s
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BurstPlanVisualizer({ plan, loading, error, onRefresh, waveId, waveStatus }) {
  if (loading) {
    return (
      <div className="rounded-xl p-10 text-center" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="burst-plan-loading">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <Sparkles className="h-6 w-6" style={{ color: T }} />
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Orchestrating bursts</p>
          <p className="text-[10px] text-gray-600">Scoring creators · Locking hook · Computing stagger · Assigning angles</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl p-6 text-center" style={{ background: CARD, border: `1px solid #5a1f1f` }}>
        <p className="text-xs text-red-400">{error}</p>
        {onRefresh && (
          <button onClick={onRefresh} className="mt-3 text-xs text-white px-3 py-1.5 rounded" style={{ background: BORDER }}>
            Retry
          </button>
        )}
      </div>
    );
  }
  if (!plan) return null;

  const band = BAND_COPY[plan.velocity_band] || { label: plan.velocity_band, tagline: '' };
  const isLive = waveStatus === 'sent';

  return (
    <div className="space-y-4" data-testid="burst-plan-visualizer">
      {/* "Watch Live" CTA — only shown once wave has been sent */}
      {isLive && waveId && (
        <a href={`/waves/${waveId}/live`}
           className="flex items-center justify-between gap-3 px-5 py-3 rounded-xl relative overflow-hidden group"
           style={{
             background: `linear-gradient(135deg, #001a22, #150022)`,
             border: `1px solid ${T}55`,
           }}
           data-testid="watch-live-cta">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-red-500 animate-ping opacity-75" />
            </div>
            <div>
              <div className="text-xs font-bold text-white">Watch this wave go live</div>
              <div className="text-[10px] text-gray-500">Real-time dashboard with engagement velocity and post feed</div>
            </div>
          </div>
          <div className="text-xs text-cyan-400 group-hover:translate-x-1 transition-transform">
            Launch →
          </div>
        </a>
      )}
      {/* Hero header — hook pill + velocity band */}
      <div className="rounded-xl p-5 relative overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${CARD}, #0f0c1c)`, border: `1px solid ${BORDER}` }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20 blur-3xl"
             style={{ background: `radial-gradient(circle, ${P}, transparent)` }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4" style={{ color: T }} />
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Wave Orchestration Plan</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Hash className="h-5 w-5" style={{ color: P }} />
              <span className="text-xl font-bold text-white font-mono">{plan.unique_hook}</span>
            </div>
            <p className="text-xs text-gray-500">{band.tagline}</p>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" style={{ color: T }} />
                <span className="text-white font-bold">{plan.total_assignments}</span>
                <span className="text-gray-500">posts</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5" style={{ color: P }} />
                <span className="text-white font-bold">{plan.total_bursts}</span>
                <span className="text-gray-500">bursts</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" style={{ color: '#FFB84D' }} />
                <span className="text-white font-bold uppercase">{plan.velocity_band}</span>
              </div>
            </div>
            {plan.wave_diversity && plan.wave_diversity.n >= 2 && (
              <div className="flex justify-end" data-testid="burst-plan-diversity">
                <DiversityScorePill diversity={plan.wave_diversity} label="Reach coverage" compact />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Predicted shape chart */}
      <PredictedShapeChart samples={plan.predicted_shape} />

      {/* Burst timeline */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Burst Timeline</span>
          <div className="h-px flex-1" style={{ background: BORDER }} />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {plan.bursts.map((b, i) => <BurstTile key={b.burst_number} burst={b} burstIndex={i} />)}
        </div>
      </div>

      {/* Rationale */}
      {plan.rationale?.length > 0 && (
        <div className="rounded-xl p-4" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="burst-plan-rationale">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-3.5 w-3.5" style={{ color: T }} />
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Why this plan</span>
          </div>
          <ul className="space-y-1.5">
            {plan.rationale.map((r) => (
              <li key={r} className="text-[11px] text-gray-300 flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: T }} />
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
